import { FC, Fragment, ReactNode, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Bars3CenterLeftIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { UpgradeBanner } from './UpgradeBanner'
import { ShutdownBanner } from './ShutdownBanner'
import { isShuttingDown } from 'lib/shutdown'
import PageLoader from './PageLoader'
import { Router, useRouter } from 'next/router'
import { useClearStateStores } from 'hooks/mutations/useClearStateStores'
import Sidebar from './Sidebar'
import { useAuth } from 'util/auth'
import { useProjectStore } from 'store/useProjectStore'
import { UploadModal } from './UploadModal'
import { NewProjectModal } from './NewProjectModal'
import { useHandleInvites } from 'hooks/project_members'
import { setUser } from 'util/sentry'
import { TrialStartedModal } from './TrialStartedModal'
import { UpgradeTrialModal } from './UpgradeTrialModal'
import { PricingTableModal } from './PricingTableModal'
import { Confetti } from './Confetti'
import { useUploadStore } from 'hooks/useUploadStore'
import { InsufficientCreditsModal } from './InsufficientCreditsModal'
import { OutcastRebrandModal } from './OutcastRebrandModal'

interface ToastyLayoutProps {
  current?: string
  children: ReactNode
  bgColor?: string
}

const ToastyLayout: FC<ToastyLayoutProps> = ({
  current = '/dashboard',
  children,
  bgColor = 'bg-white',
}) => {
  const { user } = useAuth()
  useHandleInvites({ email: user ? user.email : '' })
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const {
    showUploadModal,
    showNewProjectModal,
    showTrialStartedModal,
    showUpgradeTrialModal,
    showPricingModal,
    showConfetti,
    setShowUpgradeTrialModal,
    setShowPricingModal,
  } = useProjectStore()

  const { showInsufficientCreditsModal: shownInsufficientCreditsModal } =
    useUploadStore()

  const [loading, setLoading] = useState(false)

  const { clearStateStores } = useClearStateStores()

  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      if (!url.includes('/upload')) {
        setLoading(true)
        clearStateStores()
      }
    }

    const handleRouteChangeComplete = () => {
      setLoading(false)
    }

    Router.events.on('routeChangeStart', handleRouteChangeStart)
    Router.events.on('routeChangeComplete', handleRouteChangeComplete)
    Router.events.on('routeChangeError', handleRouteChangeComplete)

    return () => {
      Router.events.off('routeChangeStart', handleRouteChangeStart)
      Router.events.off('routeChangeComplete', handleRouteChangeComplete)
      Router.events.off('routeChangeError', handleRouteChangeComplete)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const router = useRouter()

  useEffect(() => {
    if (!user) {
      setLoading(true)
    } else if (user && !user.hasCompletedSetup) {
      setLoading(true)
      router.push('/setup')
    } else {
      setLoading(false)
    }
  }, [user, router])

  const [hasSentryUser, setHasSentryUser] = useState(false)

  // Set Sentry user
  useEffect(() => {
    if (user && !hasSentryUser) {
      setUser(user.id)
      setHasSentryUser(true)
    }
  }, [user, hasSentryUser])

  return (
    <>
      {showUploadModal && <UploadModal />}
      {showNewProjectModal && <NewProjectModal />}
      {showTrialStartedModal && <TrialStartedModal />}
      {showUpgradeTrialModal && <UpgradeTrialModal />}
      {showPricingModal && (
        <PricingTableModal
          isLoading={false}
          show="all"
          isTrialing={user ? user.hasStartedTrial : false}
          setShowUpgradeTrialModal={setShowUpgradeTrialModal}
          showPricingModal={showPricingModal}
          setShowPricingModal={setShowPricingModal}
        />
      )}
      <OutcastRebrandModal />
      <InsufficientCreditsModal />
      {showConfetti && <Confetti />}
      <div
        className={`flex min-h-full flex-1 flex-col overflow-visible font-openSans ${bgColor}`}
      >
        {isShuttingDown() ? <ShutdownBanner /> : <UpgradeBanner />}
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-40 lg:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className={`fixed inset-0 ${bgColor} bg-opacity-75`} />
            </Transition.Child>

            <div className="fixed inset-0 z-40 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel
                  className={`relative flex w-full max-w-xs flex-1 flex-col ${bgColor} pb-4 pt-5`}
                >
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute right-0 top-0 -mr-12 pt-2">
                      <button
                        type="button"
                        className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          className="h-6 w-6 text-black"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </Transition.Child>
                  <Sidebar current={current} />
                </Dialog.Panel>
              </Transition.Child>
              <div className="w-14 flex-shrink-0" aria-hidden="true">
                {/* Dummy element to force sidebar to shrink to fit close icon */}
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div
          className={`hidden h-screen border-r border-gray-200 lg:fixed lg:inset-y-0 lg:flex lg:w-52 lg:flex-col`}
        >
          <Sidebar current={current} />
        </div>
        <div className={`flex h-screen flex-col lg:pl-52`}>
          <div
            className={`flex h-16 flex-shrink-0 border-b border-gray-200 ${bgColor} lg:h-4 lg:border-none`}
          >
            <button
              type="button"
              className="border-r border-gray-200 px-4 text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3CenterLeftIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          {loading && <PageLoader size={24} hasMessage />}
          {!loading && children}
        </div>
      </div>
    </>
  )
}

export default ToastyLayout
